import React from 'react';

import { NativeResources } from '@ariksa/inventory-core';
import { AggregatedAlertDetails } from '@ariksa/notification';
import { LogActions } from '@ariksa/notification/api';
import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import { each, isEmpty } from 'lodash';
import { useSelector } from 'react-redux';

import {
  CustomTooltip,
  getIcon,
  getProviderAlias,
  renderRiskContext,
  renderSeverityBar,
  renderTimeStackedCell,
  renderTooltipFieldValue,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import { EnvironmentName } from 'containers/App/utils';
import {
  renderAlertsStatus,
  renderAlertsStatusHeader,
} from 'containers/Findings/AlertDrawer/Components/Aggregated/getAlertColumns';
import { selectAlertDrawer } from 'containers/Findings/AlertDrawer/selectors';
import { selectFindingsOverview } from 'containers/Findings/FindingsOverview/selectors';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { limitedString } from 'utils/string';

interface Props {
  aggregatedAlert: AggregatedAlertDetails;
}

export const useFindingsOverviewColumns = (props: Props) => {
  const { aggregatedAlert } = props;

  const { alertListStatus } = useSelector(selectAlertDrawer);
  const { accountMapping } = useCloudAccountId();
  const { riskContext } = useSelector(selectFindingsOverview);

  const resourceType: NativeResources | string =
    aggregatedAlert?.resource_types?.[0] ?? '';
  const multiResource = aggregatedAlert?.multi_resource ?? false;

  const entityName = {
    header: <Box pl="thLeftPaddingWithIcon">Name</Box>,
    accessor: 'entity_name',
    sortKey: 'entity_name',
    align: 'left',
    render: ({ row }) => {
      return (
        <WithResourceIcon resourceType={row.resource}>
          <StackedCell
            upper={row.entity_name ?? row.ariksa_name ?? ''}
            lower={row.entity_id}
          />
        </WithResourceIcon>
      );
    },
  };

  const workflowStatus = {
    header: renderAlertsStatusHeader(),
    accessor: 'workflow_status',
    align: 'left',
    render: ({ row }) => {
      const tickets = alertListStatus?.data?.[row?.uuid];
      let ticketIds: string[] = [];
      each(tickets, t => {
        if (t.action === LogActions.Jira || t.action === LogActions.Servicenow)
          if (!!t.ticket_id) ticketIds.push(t.ticket_id);
      });
      return (
        <StackedCell
          upper={renderAlertsStatus(tickets)}
          lower={isEmpty(ticketIds) ? '-' : ticketIds.join(', ')}
        />
      );
    },
  };

  const riskContextView = {
    header: 'Risk Context',
    accessor: 'risk_context',
    align: 'left',
    render: ({ row }) => {
      const context = riskContext.data?.[row.entity_uuid] ?? [];
      return renderRiskContext(context);
    },
  };

  const totalAlerts = {
    header: 'Total Alerts',
    align: 'left',
    render: ({ row }) => 1,
  };

  const ports = {
    header: 'Port(s)',
    align: 'left',
    render: ({ row }) => {
      const ports = row.extras?.ports ?? [];
      return (
        <CustomTooltip
          label={
            <Stack>
              <Box>
                {row.extras.traffic_state
                  ? 'Ingress traffic received on port(s)'
                  : 'No ingress traffic on port(s)'}
              </Box>
              {ports.map(v => <Box>{v}</Box>) ?? ''}
            </Stack>
          }
        >
          {limitedString(ports.join(', '))}
        </CustomTooltip>
      );
    },
  };

  const severity = {
    header: 'Severity',
    accessor: 'severity',
    align: 'left',
    sortKey: 'severity',
    render: ({ value }) => renderSeverityBar({ value, isInline: false }),
  };

  const createdAt = {
    header: 'Created At',
    accessor: 'created_at',
    align: 'left',
    render: renderTimeStackedCell,
  };

  const deployedIn = {
    header: 'Deployed In',
    accessor: 'account_id',
    sortKey: 'account_id',
    align: 'left',
    render: ({ value, row }) => {
      const account = accountMapping[value];
      return (
        <CustomTooltip
          label={
            <Stack>
              {renderTooltipFieldValue(
                'Environment',
                <EnvironmentName environmentId={row.environment_id} />,
              )}
              {renderTooltipFieldValue('Account Name', account?.name)}
              {renderTooltipFieldValue('Account ID', account?.cloud_account_id)}
              {renderTooltipFieldValue(
                'Cloud',
                getProviderAlias(account?.cloud_type),
              )}
            </Stack>
          }
        >
          <StackedCell
            upper={<EnvironmentName environmentId={row.environment_id} />}
            lower={
              <HStack w="full">
                <Box boxSize={5}>{getIcon(account?.cloud_type!)}</Box>
                <Text
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {account?.name}
                </Text>
              </HStack>
            }
            showLowerTooltip={false}
            showUpperTooltip={false}
          />
        </CustomTooltip>
      );
    },
  };

  let columns: TableColumnProps[] = [
    entityName,
    deployedIn,
    severity,
    workflowStatus,
    createdAt,
    totalAlerts,
  ];

  if (resourceType === NativeResources.Ec2Instance) {
    if (!multiResource)
      columns = [
        entityName,
        riskContextView,
        severity,
        deployedIn,
        ports,
        workflowStatus,
        createdAt,
      ];
  }

  if (!multiResource) {
    columns = [
      entityName,
      riskContextView,
      severity,
      deployedIn,
      workflowStatus,
      createdAt,
    ];
  }

  return { columns };
};
